import { flash } from "@xola/ui-kit";
import { AxiosResponse } from "axios";
import { FormField } from "common";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { forwardRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { KioskImageFieldConfig, KioskThemePreferences, KioskThemePreferenceType } from "types";
import { xolaUri } from "@/api/xolaUri";
import { ImageUploadInput } from "@/components/Common/ImageUploadInput";
import { MediaUploadInput } from "@/components/Common/MediaUploadInput";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { useSeller } from "@/hooks/useAccount";

interface KioskThemePreferencesFormProps {
    initialValues: KioskThemePreferences;
}

type MediaData = {
    id: string;
    src: string;
    type: string;
};

export const KioskThemePreferencesFields = forwardRef<
    FormikProps<KioskThemePreferences>,
    KioskThemePreferencesFormProps
>(({ initialValues }, ref) => {
    const seller = useSeller();
    const uploadURL = `${xolaUri}/api/sellers/${seller.id}/medias`;

    const [backgroundConfigured, setBackgroundConfigured] = useState(false);
    const [backgroundImageURL, setBackgroundImageURL] = useState(
        initialValues[KioskThemePreferenceType.KIOSK_HOME_BACKGROUND],
    );

    const [headerConfigured, setHeaderConfigured] = useState(false);
    const [headerImageURL, setHeaderImageURL] = useState(initialValues[KioskThemePreferenceType.KIOSK_HEADER_IMAGE]);

    const [attractConfigured, setAttractConfigured] = useState(false);
    const [attractVideoURL, setAttractVideoURL] = useState(initialValues[KioskThemePreferenceType.KIOSK_ATTRACT_VIDEO]);
    const [attractVideoFileName, setAttractVideoFileName] = useState("");

    const updateBackgroundImagePreference = (response: AxiosResponse<MediaData>) => {
        if (response.data?.id) {
            setBackgroundImageURL(`${xolaUri}${response.data.src}`);
            setBackgroundConfigured(true);
        } else {
            setBackgroundConfigured(false);
        }
    };

    const updateHeaderImagePreference = (response: AxiosResponse<MediaData>) => {
        if (response.data?.id) {
            setHeaderImageURL(`${xolaUri}${response.data.src}`);
            setHeaderConfigured(true);
        } else {
            setHeaderConfigured(false);
        }
    };

    const updateAttractVideoPreference = (response: AxiosResponse<MediaData>) => {
        if (response.data?.id) {
            setAttractVideoURL(`${xolaUri}${response.data.src}`);
            setAttractVideoFileName(response.data.src);
            setAttractConfigured(true);
        } else {
            setAttractConfigured(false);
        }
    };

    const deleteHeaderImage = () => {
        setHeaderImageURL("");
        setHeaderConfigured(true);
    };

    const deleteBackgroundImage = () => {
        setBackgroundImageURL("");
        setBackgroundConfigured(true);
    };

    const deleteAttractVideo = () => {
        setAttractVideoURL("");
        setAttractConfigured(true);
        setAttractVideoFileName("None");
    };

    const themePreferenceFields: Record<string, KioskImageFieldConfig> = {
        KIOSK_HOME_BACKGROUND: {
            name: KioskThemePreferenceType.KIOSK_HOME_BACKGROUND,
            label: "Home Screen Image",
            src: backgroundImageURL,
            uploadUrl: uploadURL,
            onUpdated: updateBackgroundImagePreference,
            hasDelete: true,
            onDelete: deleteBackgroundImage,
            tooltip:
                "Display a custom image when the kiosk is started or idle. Otherwise, the Xola logo will be displayed.",
        },
        KIOSK_HEADER_IMAGE: {
            name: KioskThemePreferenceType.KIOSK_HEADER_IMAGE,
            label: "Header Image",
            src: headerImageURL,
            uploadUrl: uploadURL,
            onUpdated: updateHeaderImagePreference,
            hasDelete: true,
            onDelete: deleteHeaderImage,
            tooltip:
                "Display a custom logo or image at the top of the kiosk screens. Only visible when the kiosk display is in the vertical (“portrait”) position.",
        },
        KIOSK_ATTRACT_VIDEO: {
            name: KioskThemePreferenceType.KIOSK_ATTRACT_VIDEO,
            label: "Attract Video",
            src: attractVideoURL,
            uploadUrl: uploadURL,
            onUpdated: updateAttractVideoPreference,
            hasDelete: true,
            onDelete: deleteAttractVideo,
            tooltip: "Display a looping video after the kiosk has been left idle on the home screen",
        },
    };

    const location = useLocation();
    useEffect(() => {
        if (!initialValues || Object.keys(initialValues).length === 0) return;

        const loadImg = async () => {
            setBackgroundImageURL(initialValues[KioskThemePreferenceType.KIOSK_HOME_BACKGROUND]);
            setHeaderImageURL(initialValues[KioskThemePreferenceType.KIOSK_HEADER_IMAGE]);
            setAttractVideoURL(initialValues[KioskThemePreferenceType.KIOSK_ATTRACT_VIDEO]);
            setAttractVideoFileName(initialValues[KioskThemePreferenceType.KIOSK_ATTRACT_VIDEO]);
        };

        loadImg();
    }, [initialValues, location.pathname]);

    const handleSubmit = async (values: KioskThemePreferences, { setValues }: FormikHelpers<KioskThemePreferences>) => {
        const modifiedValues = {
            ...values,
            ...(headerConfigured && { [KioskThemePreferenceType.KIOSK_HEADER_IMAGE]: headerImageURL }),
            ...(backgroundConfigured && { [KioskThemePreferenceType.KIOSK_HOME_BACKGROUND]: backgroundImageURL }),
            ...(attractConfigured && { [KioskThemePreferenceType.KIOSK_ATTRACT_VIDEO]: attractVideoURL }),
        };

        setValues(modifiedValues);
        return modifiedValues;
    };

    return (
        <Formik enableReinitialize innerRef={ref} initialValues={initialValues} onSubmit={handleSubmit}>
            <ContentLayout.Section title="Theme">
                {/* Home Screen Image */}
                <div
                    key={themePreferenceFields.KIOSK_HOME_BACKGROUND.name}
                    data-testid={`prefs-kiosk-${themePreferenceFields.KIOSK_HOME_BACKGROUND.label}-section`}
                >
                    <FormField
                        name={themePreferenceFields.KIOSK_HOME_BACKGROUND.name}
                        label={themePreferenceFields.KIOSK_HOME_BACKGROUND.label}
                        tooltip={themePreferenceFields.KIOSK_HOME_BACKGROUND.tooltip}
                    >
                        <ImageUploadInput
                            returnResponse
                            src={backgroundImageURL}
                            uploadUrl={uploadURL}
                            hasDelete={themePreferenceFields.KIOSK_HOME_BACKGROUND.hasDelete}
                            onDelete={themePreferenceFields.KIOSK_HOME_BACKGROUND.onDelete}
                            onUpdated={themePreferenceFields.KIOSK_HOME_BACKGROUND.onUpdated}
                            onError={(error) => {
                                return flash.show({ text: error, color: "danger" });
                            }}
                        />
                    </FormField>
                </div>

                {/* Header Image */}
                <div
                    key={themePreferenceFields.KIOSK_HEADER_IMAGE.name}
                    data-testid={`prefs-kiosk-${themePreferenceFields.KIOSK_HEADER_IMAGE.label}-section`}
                >
                    <FormField
                        name={themePreferenceFields.KIOSK_HEADER_IMAGE.name}
                        label={themePreferenceFields.KIOSK_HEADER_IMAGE.label}
                        tooltip={themePreferenceFields.KIOSK_HEADER_IMAGE.tooltip}
                    >
                        <ImageUploadInput
                            returnResponse
                            src={headerImageURL}
                            uploadUrl={uploadURL}
                            hasDelete={themePreferenceFields.KIOSK_HEADER_IMAGE.hasDelete}
                            onDelete={themePreferenceFields.KIOSK_HEADER_IMAGE.onDelete}
                            onUpdated={themePreferenceFields.KIOSK_HEADER_IMAGE.onUpdated}
                            onError={(error) => {
                                return flash.show({ text: error, color: "danger" });
                            }}
                        />
                    </FormField>
                </div>

                {/* Attract Video */}
                <div
                    key={themePreferenceFields.KIOSK_ATTRACT_VIDEO.name}
                    data-testid={`prefs-kiosk-${themePreferenceFields.KIOSK_ATTRACT_VIDEO.label}-section`}
                >
                    <FormField
                        name={themePreferenceFields.KIOSK_ATTRACT_VIDEO.name}
                        label={themePreferenceFields.KIOSK_ATTRACT_VIDEO.label}
                        tooltip={themePreferenceFields.KIOSK_ATTRACT_VIDEO.tooltip}
                    >
                        <MediaUploadInput
                            returnResponse
                            makeThumbnail
                            source={attractVideoURL}
                            filename={attractVideoFileName}
                            mediaType="video"
                            allowedExtensions={["mp4", "mkv", "mov"]}
                            maxSize={50}
                            uploadUrl={uploadURL}
                            hasDelete={themePreferenceFields.KIOSK_ATTRACT_VIDEO.hasDelete}
                            onDelete={themePreferenceFields.KIOSK_ATTRACT_VIDEO.onDelete}
                            onUpdated={themePreferenceFields.KIOSK_ATTRACT_VIDEO.onUpdated}
                            onError={(error) => {
                                return flash.show({ text: error, color: "danger" });
                            }}
                        />
                    </FormField>
                </div>
            </ContentLayout.Section>
        </Formik>
    );
});
